import { InfrastructureType } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

export interface InfrastructureSubItemEntity {
  itemId: string;
  order: number;
  infrastructureType: InfrastructureType;
  parentItemId: string;
  childItemId: string;
  orgId: string;
  projectId: string;
  createdAt: number;
  updatedAt: number;
}

/**
 * Get all sub-items for a specific infrastructure relationship
 */
export const getInfrastructureSubItemsList = async (
  orgId: string,
  projectId: string,
  parentItemId: string,
  infrastructureType: InfrastructureType,
  childItemId: string,
): Promise<InfrastructureSubItemEntity[]> => {
  const { data } = await api.get<undefined, ApiResponse<InfrastructureSubItemEntity[]>>(
    `/orgs/${orgId}/projects/${projectId}/items/${parentItemId}/infrastructures/${infrastructureType}/${childItemId}/sub-items`,
  );
  return data;
};

export const getInfrastructureSubItemsListQueryKey = (
  orgId?: string,
  projectId?: string,
  parentItemId?: string,
  infrastructureType?: InfrastructureType,
  childItemId?: string,
) => {
  const queryKey: unknown[] = ['infrastructure-sub-items-list'];
  if (orgId) queryKey.push('org', orgId);
  if (projectId) queryKey.push('project', projectId);
  if (parentItemId) queryKey.push('parent', parentItemId);
  if (infrastructureType) queryKey.push('type', infrastructureType);
  if (childItemId) queryKey.push('child', childItemId);
  return queryKey;
};

export const getInfrastructureSubItemsListQueryOptions = (
  orgId?: string,
  projectId?: string,
  parentItemId?: string,
  infrastructureType?: InfrastructureType,
  childItemId?: string,
): UseQueryOptions<InfrastructureSubItemEntity[]> => {
  return {
    queryKey: getInfrastructureSubItemsListQueryKey(
      orgId,
      projectId,
      parentItemId,
      infrastructureType,
      childItemId,
    ),
    queryFn: () =>
      getInfrastructureSubItemsList(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        orgId!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        projectId!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        parentItemId!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        infrastructureType!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        childItemId!,
      ),
    enabled: !!orgId && !!projectId && !!parentItemId && !!infrastructureType && !!childItemId,
  };
};

export const useInfrastructureSubItemsList = (
  parentItemId?: string,
  infrastructureType?: InfrastructureType,
  childItemId?: string,
  queryConfig?: Omit<UseQueryOptions<InfrastructureSubItemEntity[]>, 'queryKey' | 'queryFn'>,
) => {
  const { orgId, projectId } = useScopeIds('org', 'project');
  return useQuery({
    ...getInfrastructureSubItemsListQueryOptions(
      orgId,
      projectId,
      parentItemId,
      infrastructureType,
      childItemId,
    ),
    ...queryConfig,
  });
};
